import React, { useState, useEffect } from 'react';
import { Mail, Database, Package, ArrowUpDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  DirectMailSpendRow, 
  DataListCostRow, 
  MaterialsOtherRow,
  DateRange 
} from '../../services/ppcFarmCostService';

interface MailingCostTabbedViewerProps {
  directMailData: DirectMailSpendRow[];
  dataListData: DataListCostRow[];
  materialsOtherData: MaterialsOtherRow[];
  isLoading?: boolean;
  dateRange: DateRange;
  className?: string;
}

type TabType = 'direct-mail' | 'data-list' | 'materials-other';
type SortField = string;
type SortDirection = 'asc' | 'desc';

interface TabConfig {
  id: TabType;
  label: string;
  icon: React.ReactNode;
  description: string;
}

const TABS: TabConfig[] = [
  {
    id: 'direct-mail',
    label: 'Mailing Costs',
    icon: <Mail className="h-4 w-4" />,
    description: 'Breakdown of mailing campaign costs and cost-per-piece analysis'
  },
  {
    id: 'data-list',
    label: 'Data Scrape Costs',
    icon: <Database className="h-4 w-4" />,
    description: 'Expenses for acquiring contact lists and data scraping'
  },
  {
    id: 'materials-other',
    label: 'Props & Other',
    icon: <Package className="h-4 w-4" />,
    description: 'Supplementary expenses like props, magnets, and overhead'
  }
];

export const MailingCostTabbedViewer: React.FC<MailingCostTabbedViewerProps> = ({
  directMailData,
  dataListData,
  materialsOtherData,
  isLoading = false,
  dateRange,
  className
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('direct-mail');
  const [sortField, setSortField] = useState<SortField>('');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Reset sort when tab changes
  useEffect(() => {
    setSortField('');
    setSortDirection('desc');
  }, [activeTab]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortedData = (data: any[], field: string, direction: SortDirection) => {
    if (!field) return data;
    
    return [...data].sort((a, b) => {
      let aVal = a[field];
      let bVal = b[field];
      
      // Handle numeric sorting
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // Handle string sorting
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
      
      if (direction === 'asc') {
        return aVal.localeCompare(bVal);
      } else {
        return bVal.localeCompare(aVal);
      }
    });
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatCostPerMailer = (cost: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    }).format(cost);
  };

  const SortableHeader: React.FC<{ field: string; children: React.ReactNode }> = ({ field, children }) => (
    <th 
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        <ArrowUpDown className={`h-3 w-3 ${sortField === field ? 'text-yellow-500' : 'text-gray-400'}`} />
      </div>
    </th>
  );

  const renderDirectMailTable = () => {
    const sortedData = getSortedData(directMailData, sortField, sortDirection);
    
    if (isLoading) {
      return (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="animate-pulse flex space-x-4 p-4">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
            </div>
          ))}
        </div>
      );
    }

    if (sortedData.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          No direct mail campaigns found for the selected date range.
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <SortableHeader field="cohort">Cohort</SortableHeader>
              <SortableHeader field="dateMailed">Date Mailed</SortableHeader>
              <SortableHeader field="quantitySent">Cohort Size</SortableHeader>
              <SortableHeader field="mailingCost">Mailing Cost</SortableHeader>
              <SortableHeader field="costPerMailer">Cost Per Mail</SortableHeader>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {sortedData.map((row, index) => (
              <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  {row.cohort}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {row.dateMailed}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {row.quantitySent.toLocaleString()}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  {formatCurrency(row.mailingCost)}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200">
                    {formatCostPerMailer(row.costPerMailer)}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderDataListTable = () => {
    const sortedData = getSortedData(dataListData, sortField, sortDirection);
    
    if (isLoading) {
      return (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="animate-pulse flex space-x-4 p-4">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
            </div>
          ))}
        </div>
      );
    }

    if (sortedData.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          No data scrape expenses found for the selected date range.
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <SortableHeader field="expenseDate">Date</SortableHeader>
              <SortableHeader field="item">Item</SortableHeader>
              <SortableHeader field="amount">Amount</SortableHeader>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {sortedData.map((row, index) => (
              <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {row.expenseDate}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {row.item}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  {formatCurrency(row.amount)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderMaterialsOtherTable = () => {
    const sortedData = getSortedData(materialsOtherData, sortField, sortDirection);
    
    if (isLoading) {
      return (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="animate-pulse flex space-x-4 p-4">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
            </div>
          ))}
        </div>
      );
    }

    if (sortedData.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          No materials or other expenses found for the selected date range.
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <SortableHeader field="expenseDate">Date</SortableHeader>
              <SortableHeader field="item">Item</SortableHeader>
              <SortableHeader field="amount">Amount</SortableHeader>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {sortedData.map((row, index) => (
              <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {row.expenseDate}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {row.item}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  {formatCurrency(row.amount)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderActiveTable = () => {
    switch (activeTab) {
      case 'direct-mail':
        return renderDirectMailTable();
      case 'data-list':
        return renderDataListTable();
      case 'materials-other':
        return renderMaterialsOtherTable();
      default:
        return null;
    }
  };

  const getActiveTabConfig = () => TABS.find(tab => tab.id === activeTab);
  const activeTabConfig = getActiveTabConfig();

  return (
    <Card className={className}>
      <CardContent className="pt-6">
        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 dark:border-gray-700">
          {TABS.map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 mb-2 ${
                activeTab === tab.id 
                  ? 'bg-yellow-500 hover:bg-yellow-600 text-white border-b-2 border-yellow-500' 
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </Button>
          ))}
        </div>

        {/* Active Table */}
        <div className="min-h-[400px]">
          {renderActiveTable()}
        </div>
      </CardContent>
    </Card>
  );
};

export default MailingCostTabbedViewer;